// React
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { ReactSVG } from "react-svg";

// Helpers
import { classNames, transformRoute, useTranslate } from "helpers";

import { VEHICLE_TABS } from "./consts";
import { TabMenuProps } from "./types";

/**
 * Tabovi vozila. Na uskom ekranu se popis pomiče vodoravno umjesto da se natpisi lome u dva retka;
 * traka za pomicanje je skrivena, a da se vidi kako popis ide dalje, rub prelazi u bijelo.
 */
const TabMenu: React.FC<TabMenuProps> = (props) => {
  const { vehicleId } = props;

  const { t } = useTranslate();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasMoreRight, setHasMoreRight] = useState<boolean>(false);

  /** Ima li još tabova desno od vidljivog dijela — po tome se pali preljev na rubu. */
  const updateOverflow = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    setHasMoreRight(container.scrollWidth - container.clientWidth - container.scrollLeft > 1);
  }, []);

  // Otvaranjem stranice aktivni tab mora biti u vidnom polju — inače se na uskom ekranu vidi
  // početak popisa, a otvoren je tab koji je negdje desno.
  useEffect(() => {
    const active = containerRef.current?.querySelector<HTMLElement>("a[aria-current='page']");

    active?.scrollIntoView({ inline: "nearest", block: "nearest" });
    updateOverflow();
  }, [vehicleId, updateOverflow]);

  useEffect(() => {
    const container = containerRef.current;

    container?.addEventListener("scroll", updateOverflow);
    window.addEventListener("resize", updateOverflow);

    return () => {
      container?.removeEventListener("scroll", updateOverflow);
      window.removeEventListener("resize", updateOverflow);
    };
  }, [updateOverflow]);

  return (
    <div className="relative border-b border-slate-200 bg-white">
      <div ref={containerRef} className="flex overflow-x-auto scrollbar-hide">
        {VEHICLE_TABS.map((tab) => (
          <NavLink
            key={tab.route}
            to={transformRoute(tab.route, { id: vehicleId })}
            className="shrink-0"
          >
            {({ isActive }) => (
              <div
                className={classNames(
                  "group flex items-center gap-2 whitespace-nowrap font-semibold pl-2 pr-4 py-2 ml-2 first:ml-3 hover:text-sky-600",
                  isActive
                    ? "text-sky-600 border-b-2 border-b-sky-600"
                    : "text-gray-600"
                )}
              >
                <ReactSVG
                  src={tab.icon}
                  className={classNames(
                    "w-5 h-5 group-hover:stroke-sky-600",
                    isActive ? "stroke-sky-600" : "stroke-slate-500"
                  )}
                />
                {t(tab.label)}
              </div>
            )}
          </NavLink>
        ))}
      </div>

      {/* Preljev na desnom rubu govori da popis ide dalje; ne prima klik, pa ne smeta tabovima. */}
      {hasMoreRight && (
        <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
      )}
    </div>
  );
};

export default memo(TabMenu);
